import { useEffect, useState } from 'react';
import {
  DashboardOutlined,
  SchoolOutlined,
  TimelineOutlined,
} from '@mui/icons-material';
import { Box, ButtonBase, Paper } from '@mui/material';
import DashboardActivities from './dashboard/DashboardActivities';
import DashboardClasses from './dashboard/DashboardClasses';
import DashboardOverview from './dashboard/DashboardOverview';
import {
  durationOptions,
  isMonthlyGroupingDisabled,
} from './dashboard/dashboardOverviewUtils';
import './DashboardPage.css';

const dashboardSections = [
  {
    id: 'activities',
    label: 'Activities',
    Icon: TimelineOutlined,
  },
  {
    id: 'overview',
    label: 'Overview',
    Icon: DashboardOutlined,
  },
  {
    id: 'classes',
    label: 'Classes',
    Icon: SchoolOutlined,
  },
];

const dashboardConfigStorageKey = 'kajTrackerDashboardConfig';
const defaultDashboardConfig = {
  period: 'week',
  duration: '30d',
};
const periodOptions = new Set(['week', 'month']);
const durationOptionValues = new Set(durationOptions.map((option) => option.value));

const normalizeDashboardConfig = (config) => {
  const duration = durationOptionValues.has(config?.duration)
    ? config.duration
    : defaultDashboardConfig.duration;
  const period = periodOptions.has(config?.period)
    ? config.period
    : defaultDashboardConfig.period;

  return {
    period: period === 'month' && isMonthlyGroupingDisabled(duration) ? 'week' : period,
    duration,
  };
};

const getSavedDashboardConfig = () => {
  try {
    if (typeof window === 'undefined') {
      return defaultDashboardConfig;
    }

    const savedConfig = JSON.parse(window.localStorage.getItem(dashboardConfigStorageKey));

    return normalizeDashboardConfig(savedConfig);
  } catch {
    return defaultDashboardConfig;
  }
};

function DashboardPage() {
  const [activeSection, setActiveSection] = useState(dashboardSections[1].id);
  const [dashboardConfig, setDashboardConfig] = useState(getSavedDashboardConfig);
  const activeSectionLabel = dashboardSections.find(({ id }) => id === activeSection)?.label;
  const { period, duration } = dashboardConfig;

  useEffect(() => {
    try {
      window.localStorage.setItem(
        dashboardConfigStorageKey,
        JSON.stringify(dashboardConfig),
      );
    } catch {
      // Keep the dashboard usable if browser storage is unavailable.
    }
  }, [dashboardConfig]);

  const setPeriod = (nextPeriod) => {
    setDashboardConfig((currentConfig) => ({
      ...currentConfig,
      period: nextPeriod === 'month' && isMonthlyGroupingDisabled(currentConfig.duration)
        ? 'week'
        : nextPeriod,
    }));
  };

  const setDuration = (nextDuration) => {
    setDashboardConfig((currentConfig) => ({
      ...currentConfig,
      period: isMonthlyGroupingDisabled(nextDuration) ? 'week' : currentConfig.period,
      duration: nextDuration,
    }));
  };

  return (
    <main className="dashboard-page">
      <Paper className="dashboard-section-nav" elevation={0}>
        {dashboardSections.map(({ id, label, Icon }) => {
          const isActive = activeSection === id;

          return (
            <ButtonBase
              key={id}
              className={`dashboard-section-button${isActive ? ' dashboard-section-button-active' : ''}`}
              onClick={() => setActiveSection(id)}
              aria-pressed={isActive}
            >
              <Icon className="dashboard-section-icon" fontSize="small" aria-hidden="true" />
              <span>{label}</span>
            </ButtonBase>
          );
        })}
      </Paper>

      <Box
        className="dashboard-section-content"
        role="region"
        aria-label={`${activeSectionLabel} dashboard section`}
      >
        {activeSection === 'activities' && (
          <DashboardActivities
            period={period}
            duration={duration}
            setPeriod={setPeriod}
            setDuration={setDuration}
          />
        )}
        {activeSection === 'classes' && (
          <DashboardClasses
            period={period}
            duration={duration}
            setPeriod={setPeriod}
            setDuration={setDuration}
          />
        )}
        {activeSection === 'overview' && (
          <DashboardOverview
            period={period}
            duration={duration}
            setPeriod={setPeriod}
            setDuration={setDuration}
          />
        )}
      </Box>
    </main>
  );
}

export default DashboardPage;
